import { useMemo } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeftRight, Plus } from 'lucide-react'

import type { ListNotesParams, NoteStatus } from '@/lib/api/product-lot-api'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { defineListFilters } from '@/components/common/filters/core'
import {
    noteFromDateFilter,
    noteSearchFilter,
    noteStatusFilter,
    noteToDateFilter,
} from '@/components/common/filters/presets/note-filters'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import {
    NOTE_CARD_CODE_CLASS,
    NoteListCard,
} from '@/components/common/NoteListCard'
import { ProductLotNotePageHeader } from '@/components/product-lots/ProductLotNotePageHeader'
import { ProductLotStatusBadge } from '@/components/product-lots/ProductLotStatusBadge'
import {
    NOTE_LIST_TABLE_CLASS,
    NOTE_LIST_WRAP_CELL_CLASS,
    NoteProductSummaryCells,
    NoteProductSummaryList,
} from '@/components/product-lots/NoteProductSummary'
import { Button } from '@/components/ui/button'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useIsMobile } from '@/hooks/use-is-mobile'
import {
    useNoteListSearchTracking,
    useNoteProductSummaryTracking,
} from '@/hooks/use-note-product-summary-tracking'
import {
    useProductLotTransferNotesInfiniteQuery,
    useProductLotTransferNotesQuery,
} from '@/hooks/use-product-lot-query'
import { formatDateTimeVN } from '@/lib/format'
import {
    LIST_SEARCH_KEYS,
    useRememberListSearch,
} from '@/lib/list-search-store'
import { m } from '@/lib/paraglide/messages'

/**
 * Search params của màn — giữ tên khoá cũ để link/bookmark cũ vẫn chạy. Trang
 * chi tiết đọc lại qua `recallListSearch<Search>` để nút Quay lại giữ bộ lọc.
 */
export type Search = {
    q?: string
    status?: NoteStatus
    from_date?: string
    to_date?: string
    page?: number
}

/** Bộ lọc khai một lần, suy ra: parser URL, mặc định, badge đếm, chip. */
const FILTERS = defineListFilters({
    q: noteSearchFilter(),
    status: noteStatusFilter(),
    from_date: noteFromDateFilter(),
    to_date: noteToDateFilter(),
})

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/sales-warehouse/transfer-notes/',
)({
    component: Page,
    validateSearch: FILTERS.validateSearch,
})

function Page() {
    const search = Route.useSearch()
    useRememberListSearch(LIST_SEARCH_KEYS.transferNotes, search)
    const navigate = useNavigate({
        from: '/sales-warehouse/transfer-notes/',
    })

    const { values, barProps, page, setPage } = useListFilters({
        def: FILTERS,
        search,
        onSearchChange: (next, opts) =>
            void navigate({
                search: () => next,
                replace: opts?.replace ?? false,
            }),
        columns: 3,
    })

    // Map bộ lọc → tham số API. Để tường minh ở màn: mỗi màn map một kiểu.
    const apiFilters = useMemo(
        () => ({
            ...(values.q ? { search: values.q } : {}),
            ...(values.status !== 'all' ? { status: values.status } : {}),
            ...(values.from_date ? { date_from: values.from_date } : {}),
            ...(values.to_date ? { date_to: values.to_date } : {}),
        }),
        [values.q, values.status, values.from_date, values.to_date],
    )

    const params = useMemo<ListNotesParams>(
        () => ({ page, per_page: 50, ...apiFilters }),
        [page, apiFilters],
    )

    const isMobile = useIsMobile()

    const { data, isLoading, isFetching, refetch } =
        useProductLotTransferNotesQuery(params, { enabled: !isMobile })

    const infiniteParams = useMemo(
        () => ({ per_page: 50, ...apiFilters }),
        [apiFilters],
    )
    const infinite = useProductLotTransferNotesInfiniteQuery(infiniteParams, {
        enabled: isMobile,
    })
    const mobileNotes = useMemo(
        () => infinite.data?.pages.flatMap((p) => p.data ?? []) ?? [],
        [infinite.data],
    )

    const notes = data?.data ?? []
    useNoteProductSummaryTracking({
        kind: 'transfer',
        layout: 'table',
        notes,
        enabled: !isMobile,
    })
    useNoteProductSummaryTracking({
        kind: 'transfer',
        layout: 'cards',
        notes: mobileNotes,
        enabled: isMobile,
    })
    const total = data?.meta?.total ?? 0
    useNoteListSearchTracking({
        kind: 'transfer',
        layout: 'table',
        query: values.q,
        total,
        ready: !!data && !isFetching,
        withFilters: barProps.activeCount > 0,
        enabled: !isMobile,
    })
    useNoteListSearchTracking({
        kind: 'transfer',
        layout: 'cards',
        query: values.q,
        total: infinite.data?.pages[0]?.meta?.total ?? 0,
        ready: !!infinite.data && !infinite.isFetching,
        withFilters: barProps.activeCount > 0,
        enabled: isMobile,
    })

    // Ô tìm kiếm nằm ngoài sheet nên không vào `activeCount`; rỗng vì từ khoá
    // cũng là rỗng vì bộ lọc (R10).
    const filtered = barProps.activeCount > 0 || !!values.q

    // Rỗng vì chưa có dữ liệu khác hẳn rỗng vì bộ lọc quá hẹp — trường hợp thứ
    // hai phải cho người dùng lối thoát ngay tại chỗ (R10).
    const emptyState = (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
            <span className="text-muted-foreground">
                {filtered ? m.list_empty_filtered() : m.sl_note_empty()}
            </span>
            {filtered ? (
                <ClearFilterButton active onClear={barProps.onClear} />
            ) : null}
        </div>
    )

    return (
        <div className="space-y-4 page-pad">
            <ProductLotNotePageHeader
                icon={<ArrowLeftRight className="size-5 shrink-0" />}
                title={m.pdc_list_title()}
                primaryActions={
                    <Button asChild>
                        <Link
                            to="/sales-warehouse/transfer-notes/new"
                            className="w-full"
                        >
                            <Plus className="size-4 mr-1" />
                            {m.pdc_list_new()}
                        </Link>
                    </Button>
                }
            />

            <ListFilterBar
                {...barProps}
                clearActive={filtered}
                onRefresh={() => void refetch()}
                refreshing={isFetching}
            />

            {/* Mobile: card list */}
            <div className="space-y-2 md:hidden">
                {infinite.isLoading ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.sl_lots_loading()}
                    </div>
                ) : mobileNotes.length === 0 ? (
                    emptyState
                ) : (
                    <>
                        {mobileNotes.map((n) => (
                            <NoteListCard
                                key={n.id}
                                onClick={() =>
                                    void navigate({
                                        to: '/sales-warehouse/transfer-notes/$id',
                                        params: { id: n.id },
                                    })
                                }
                                code={
                                    <Link
                                        to="/sales-warehouse/transfer-notes/$id"
                                        params={{ id: n.id }}
                                        className={NOTE_CARD_CODE_CLASS}
                                    >
                                        {n.code}
                                    </Link>
                                }
                                statusBadge={
                                    <ProductLotStatusBadge status={n.status} />
                                }
                                totalLots={n.items_count ?? 0}
                                summary={
                                    <NoteProductSummaryList
                                        summary={n.product_summary}
                                    />
                                }
                                createdBy={n.created_by_name ?? undefined}
                                createdAt={n.created_at}
                            />
                        ))}
                        {infinite.hasNextPage && (
                            <Button
                                variant="outline"
                                className="w-full h-11"
                                disabled={infinite.isFetchingNextPage}
                                onClick={() => void infinite.fetchNextPage()}
                            >
                                {infinite.isFetchingNextPage
                                    ? m.list_loading_more()
                                    : m.list_load_more()}
                            </Button>
                        )}
                    </>
                )}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block rounded-md border bg-card overflow-x-auto">
                <Table className={NOTE_LIST_TABLE_CLASS}>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.sl_note_col_code()}</TableHead>
                            <TableHead>{m.sl_note_col_status()}</TableHead>
                            <TableHead>{m.sl_note_col_product()}</TableHead>
                            <TableHead className="text-right">
                                {m.sl_note_col_quantity()}
                            </TableHead>
                            <TableHead className="text-center">
                                {m.sl_note_col_items()}
                            </TableHead>
                            <TableHead>{m.sl_note_col_created_by()}</TableHead>
                            <TableHead>{m.sl_note_col_created_at()}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={7}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.sl_lots_loading()}
                                </TableCell>
                            </TableRow>
                        ) : notes.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7}>{emptyState}</TableCell>
                            </TableRow>
                        ) : (
                            notes.map((n) => (
                                <TableRow
                                    key={n.id}
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() =>
                                        void navigate({
                                            to: '/sales-warehouse/transfer-notes/$id',
                                            params: { id: n.id },
                                        })
                                    }
                                >
                                    <TableCell className="font-mono text-sm">
                                        <Link
                                            to="/sales-warehouse/transfer-notes/$id"
                                            params={{ id: n.id }}
                                            className="text-primary hover:underline"
                                        >
                                            {n.code}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        <ProductLotStatusBadge
                                            status={n.status}
                                        />
                                    </TableCell>
                                    <NoteProductSummaryCells
                                        summary={n.product_summary}
                                    />
                                    <TableCell className="text-center">
                                        {n.items_count ?? 0}
                                    </TableCell>
                                    <TableCell
                                        className={`${NOTE_LIST_WRAP_CELL_CLASS} min-w-24`}
                                    >
                                        {n.created_by_name ?? '—'}
                                    </TableCell>
                                    <TableCell
                                        className={`${NOTE_LIST_WRAP_CELL_CLASS} min-w-20 text-sm text-muted-foreground`}
                                    >
                                        {formatDateTimeVN(n.created_at)}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination */}
            <div className="hidden md:flex items-center justify-between">
                <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(Math.max(1, page - 1))}
                >
                    {m.sl_pagination_prev()}
                </Button>
                <div className="text-sm text-muted-foreground">
                    {m.sl_pagination_page({
                        page,
                        total: Math.max(1, Math.ceil(total / 50)),
                    })}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    disabled={!total || page * 50 >= total}
                    onClick={() => setPage(page + 1)}
                >
                    {m.sl_pagination_next()}
                </Button>
            </div>
        </div>
    )
}
